import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, WarningCircleIcon as WarningCircle, CircleDashedIcon as CircleDashed, ArrowsClockwiseIcon as ArrowsClockwise, PaperPlaneTiltIcon as PaperPlaneTilt, RobotIcon as Robot, ShieldCheckIcon as ShieldCheck, CopyIcon as Copy, EnvelopeIcon as Envelope } from "@phosphor-icons/react";
import { Icon } from "@/components/luxury";

/**
 * 7-step kennel verification drawer.
 *
 * Opens from the admin's Breeders list / BreederDetail. Lets admins:
 *   - See every step's current status with one-line evidence note
 *   - Refresh the three automated checks on demand
 *   - Mark manual steps (identity, premises, breeding_history) passed/rejected/needs_info with notes
 *   - Trigger reference-request emails to two recent buyers
 *   - Confer the verified seal once all seven steps pass
 *
 * When a step has been auto-decided ("passed" or "rejected"), the manual
 * status select is hidden and the source-of-truth note is shown instead.
 */

const STATUS_META = {
  pending:    { label: "Pending",     tone: "muted",   icon: CircleDashed },
  passed:     { label: "Passed",      tone: "gold",    icon: CheckCircle },
  rejected:   { label: "Rejected",    tone: "crimson", icon: XCircle },
  needs_info: { label: "Needs info",  tone: "crimson", icon: WarningCircle },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const cls = {
    muted:   "bg-[#F4F4F4] text-[#6B6B6B] border-[#E5E5E5]",
    gold:    "bg-[#FBF7EC] text-[#8A6A00] border-[#E5D08A]",
    crimson: "bg-[#FBEBEC] text-[#A4222A] border-[#E5BEC0]",
  }[meta.tone];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.2em] border ${cls}`}>
      <meta.icon size={12} weight={status === "passed" ? "fill" : "duotone"} />
      {meta.label}
    </span>
  );
}

// Renders a thumbnail for an evidence URL — image inline, PDF as a
// red "PDF" tile so the admin can tell at a glance what was uploaded.
function EvidencePreview({ url, alt, large = false }) {
  const isPdf = (url || "").toLowerCase().split("?")[0].endsWith(".pdf");
  const sizeClass = large ? "h-20 w-full" : "h-12 w-12";
  if (isPdf) {
    return (
      <div className={`${sizeClass} flex items-center justify-center rounded border border-crimson/30 bg-crimson/5 text-crimson text-[10px] font-bold tracking-[0.1em] uppercase`}>
        PDF
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      className={`${sizeClass} object-cover rounded border border-[#EAEAEA]`}
    />
  );
}


/**
 * Owner-action panel rendered inside the Stripe Connect step card
 * whenever the kennel owner hasn't completed Connect onboarding.
 *
 * Why it exists: the automated check correctly detects "owner hasn't
 * onboarded yet" but used to leave the admin with no recourse other
 * than the awkward instruction to "send a Connect link manually" —
 * which on prod meant copy-pasting a URL out of the Stripe dashboard
 * by hand. This panel mints the link server-side (under the owner's
 * Stripe account, not the admin's) and emails it to the owner with
 * branded copy, OR copies it to the clipboard if the admin prefers
 * to send via SMS/iMessage/another channel.
 *
 * Every mint is logged to `admin_audit` server-side so the admin
 * team has a record of who chased the owner and when.
 */
/**
 * Link Owner panel — rendered inside the Stripe Connect step card when
 * the kennel has NO linked user (`owner_resolved === false`).
 *
 * Why it exists: when the resolver returns None (typical case — kennel
 * created anonymously by a buyer registering a pup, breeder later joined
 * under their own account but the FK was never written), the admin used
 * to have to navigate away to `/admin/breeders/:id`, find the right
 * user, write the email into the kennel's Contact, and come back. This
 * panel does the same thing in one shot: search by email, pick the
 * user, server writes `breeders.user_id`, doc reloads.
 */
function LinkOwnerPanel({ breederId, onLinked }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/admin/data/users", { params: { q, limit: 6 } });
        if (!cancelled) setResults(data.items || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const link = async (user) => {
    setLinking(true);
    try {
      const { data } = await api.post(
        `/admin/kennel-verification/${breederId}/link-owner`,
        { user_id: user.id },
      );
      toast.success(`Linked ${data.linked_user_email || user.email} as kennel owner.`);
      setQ("");
      setResults([]);
      onLinked?.();
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't link the owner."));
    } finally {
      setLinking(false);
    }
  };

  return (
    <div
      className="bg-[#FBEBEC] border border-[#E5BEC0] rounded-lg p-3 mb-3 space-y-2"
      data-testid="link-owner-panel"
    >
      <p className="text-[11.5px] text-[#3F3F3F] leading-relaxed">
        <b>This kennel isn't linked to a user account.</b> Search by email or
        name and pick the right person — we'll write the link and re-run the
        Stripe Connect check in one step.
      </p>
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by email or name…"
        className="w-full rounded-md border border-[#E5BEC0] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#A4222A]/20"
        data-testid="link-owner-search-input"
        disabled={linking}
      />
      {searching && (
        <p className="text-[11px] text-[#6B6B6B] italic">Searching…</p>
      )}
      {!searching && results.length > 0 && (
        <ul className="bg-white border border-[#EAEAEA] rounded-md max-h-48 overflow-y-auto divide-y divide-[#F2F2F2]">
          {results.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                onClick={() => link(u)}
                disabled={linking}
                className="w-full text-left px-3 py-2 hover:bg-[#FAFAFA] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`link-owner-pick-${u.id}`}
              >
                <div className="text-sm text-ink font-semibold truncate">
                  {u.name || u.email}
                </div>
                <div className="text-[11px] text-[#6B6B6B] truncate">
                  {u.email}
                  {u.plan ? ` · ${u.plan}` : ""}
                  {u.dog_count ? ` · ${u.dog_count} dog${u.dog_count === 1 ? "" : "s"}` : ""}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!searching && q.trim().length >= 2 && results.length === 0 && (
        <p className="text-[11px] text-[#6B6B6B] italic">No matching users.</p>
      )}
    </div>
  );
}

function StripeConnectInvitePanel({ breederId }) {
  const [sending, setSending] = useState(false);
  const [lastUrl, setLastUrl] = useState("");

  const mint = async (sendEmail) => {
    setSending(true);
    try {
      const returnUrl = `${webOrigin()}/stripe-connect/return`;
      const { data } = await api.post(
        `/admin/kennel-verification/${breederId}/stripe-connect/invite-link`,
        { return_url: returnUrl, send_email: sendEmail },
      );
      setLastUrl(data.url || "");
      if (sendEmail) {
        toast.success(
          data.email_sent
            ? `Onboarding link emailed to ${data.owner_email}`
            : `Link generated, but the email failed. Use Copy link instead.`,
        );
      } else if (data.url) {
        try {
          await navigator.clipboard.writeText(data.url);
          toast.success("Onboarding link copied to clipboard.");
        } catch {
          toast.success("Onboarding link generated — see below.");
        }
      }
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't generate the onboarding link."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="bg-[#FBF7EC] border border-[#E8D9A6] rounded-lg p-3 mb-3 space-y-2"
      data-testid="stripe-connect-invite-panel"
    >
      <p className="text-[11.5px] text-[#3F3F3F] leading-relaxed">
        <b>Need to chase the owner?</b> Mint a fresh Stripe Connect onboarding
        link for them. Email goes through Resend with branded copy; copy-link
        lets you send via SMS or another channel.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={() => mint(true)}
          disabled={sending}
          className="bg-ink hover:bg-crimson text-white rounded-full h-8 px-3 text-xs"
          data-testid="stripe-connect-invite-email-btn"
        >
          <Envelope size={13} weight="duotone" className="mr-1.5" />
          {sending ? "Sending…" : "Email link to owner"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => mint(false)}
          disabled={sending}
          className="rounded-full h-8 px-3 text-xs border-[#E5E5E5]"
          data-testid="stripe-connect-invite-copy-btn"
        >
          <Copy size={13} weight="duotone" className="mr-1.5" />
          Copy link
        </Button>
      </div>
      {lastUrl && (
        <p
          className="text-[10.5px] text-[#6B6B6B] break-all font-mono pt-1.5 border-t border-[#E8D9A6]/60"
          data-testid="stripe-connect-invite-link-preview"
        >
          {lastUrl}
        </p>
      )}
    </div>
  );
}


function StepCard({ stepKey, label, step, breederId, onUpdate, onRefresh, onUpload, onDeleteEvidence, onLinked, busy }) {
  const [draftStatus, setDraftStatus] = useState(step.status || "pending");
  const [draftNotes, setDraftNotes] = useState(step.notes || "");
  // Only microchip + pedigree are STRICTLY automated (derived from
  // registry data — a manual flip would mask a real conflict). Every
  // other step — including Stripe Connect, which the backend stamps
  // with `automated: true` to drive its auto-derived display fields —
  // can be manually overridden by an admin, because edge cases exist
  // (a registry-only kennel that doesn't need Connect at all, an
  // owner with a Stripe account on a different platform login, etc.).
  //
  // Older code OR'd `step.automated` into this, but the backend
  // unconditionally stamps `automated: true` on `stripe_connect`, so
  // that OR short-circuited to true and hid the override controls —
  // the very bug Lauren reported on 2026-05-27 ("can't complete the
  // last one, no manual override"). Hard-code the rule by step key.
  const automated = ["microchip", "pedigree"].includes(stepKey);
  const wasAutoChecked = ["microchip", "pedigree", "stripe_connect"].includes(stepKey);

  useEffect(() => {
    setDraftStatus(step.status || "pending");
    setDraftNotes(step.notes || "");
  }, [step.status, step.notes]);

  return (
    <div className="bg-white border border-[#EAEAEA] rounded-xl p-5" data-testid={`verify-step-${stepKey}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-1.5">
            {automated ? "Automated check" : wasAutoChecked ? "Auto + override" : "Manual review"}
          </p>
          <h4 className="font-spectral text-lg tracking-tight">{label}</h4>
        </div>
        <StatusPill status={step.status || "pending"} />
      </div>

      {step.notes && (
        <p className="text-sm text-[#3a3a3a] leading-relaxed mb-3">{step.notes}</p>
      )}

      {/* Step-specific evidence panels */}
      {stepKey === "breeding_history" && (step.flags || []).length > 0 && (
        <ul className="text-xs text-[#A4222A] bg-[#FBEBEC] border border-[#E5BEC0] rounded-md p-3 mb-3 space-y-1">
          {step.flags.map((f, i) => <li key={i} className="flex gap-2"><span>·</span>{f}</li>)}
        </ul>
      )}
      {stepKey === "stripe_connect" && (
        <>
          <div className="text-xs text-[#6B6B6B] grid grid-cols-3 gap-2 mb-3">
            <div className="bg-[#FAFAFA] rounded p-2 border border-[#EAEAEA] text-center">
              <div className="font-bold">{step.charges_enabled ? "✓" : "✗"}</div>
              <div>charges</div>
            </div>
            <div className="bg-[#FAFAFA] rounded p-2 border border-[#EAEAEA] text-center">
              <div className="font-bold">{step.payouts_enabled ? "✓" : "✗"}</div>
              <div>payouts</div>
            </div>
            <div className="bg-[#FAFAFA] rounded p-2 border border-[#EAEAEA] text-center">
              <div className="font-bold">{step.details_submitted ? "✓" : "✗"}</div>
              <div>submitted</div>
            </div>
          </div>
          {/* No linked user account → admin must link first; without a
              user there's no Stripe Connect account to evaluate or
              email an onboarding link to. */}
          {step.owner_resolved === false && (
            <LinkOwnerPanel breederId={breederId} onLinked={onLinked} />
          )}
          {/* Owner-action panel — visible whenever the owner hasn't
              finished Connect onboarding. Lets the admin either email
              the owner a fresh onboarding link OR copy it to send via
              another channel. Without this, the only resolution path
              was "manually mark passed", which loses the audit trail
              of who chased the owner and when. */}
          {step.owner_resolved !== false &&
            !(step.charges_enabled && step.payouts_enabled && step.details_submitted) && (
            <StripeConnectInvitePanel breederId={breederId} />
          )}
        </>
      )}
      {stepKey === "identity" && step.evidence_url && (
        <div className="mb-3 flex items-center gap-2 bg-[#FAFAFA] border border-[#EAEAEA] rounded-lg p-2">
          <EvidencePreview url={step.evidence_url} alt="ID document" />
          <div className="flex-1 min-w-0">
            <a href={step.evidence_url} target="_blank" rel="noreferrer" className="block text-xs text-ink font-semibold hover:text-crimson truncate">
              View ID document →
            </a>
            <p className="text-[10.5px] text-[#6B6B6B] mt-0.5">Uploaded · click thumbnail to open</p>
          </div>
          <button
            type="button"
            onClick={() => onDeleteEvidence?.(stepKey, step.evidence_url)}
            className="text-[10px] font-bold uppercase tracking-[0.1em] text-crimson hover:underline px-2"
            data-testid={`verify-step-${stepKey}-evidence-delete`}
          >
            Remove
          </button>
        </div>
      )}
      {stepKey === "premises" && (step.evidence_urls || []).length > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-3">
          {step.evidence_urls.map((u, i) => (
            <div key={i} className="relative group">
              <a href={u} target="_blank" rel="noreferrer" className="block">
                <EvidencePreview url={u} alt={`premises ${i + 1}`} large />
              </a>
              <button
                type="button"
                onClick={() => onDeleteEvidence?.(stepKey, u)}
                className="absolute top-1 right-1 h-5 w-5 rounded-full bg-ink/85 text-white text-[10px] font-bold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove this photo"
                data-testid={`verify-step-${stepKey}-evidence-delete-${i}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      {stepKey === "references" && (step.requests || []).length > 0 && (
        <div className="space-y-2 mb-3">
          {step.requests.map((r, i) => (
            <div key={i} className="text-xs bg-[#FAFAFA] border border-[#EAEAEA] rounded p-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[#3a3a3a]">{r.buyer_email}</span>
                <span className={`uppercase tracking-[0.15em] font-bold text-[10px] ${
                  r.outcome === "ok" ? "text-[#8A6A00]" :
                  r.outcome === "concern" ? "text-[#A4222A]" : "text-[#6B6B6B]"
                }`}>
                  {r.outcome ? r.outcome : "awaiting"}
                </span>
              </div>
              {r.comment && <p className="mt-1 text-[#3a3a3a] italic">"{r.comment}"</p>}
            </div>
          ))}
        </div>
      )}

      {/* Manual step controls */}
      {!automated && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            {["passed", "needs_info", "rejected"].map((s) => (
              <button
                key={s}
                onClick={() => setDraftStatus(s)}
                data-testid={`verify-step-${stepKey}-set-${s}`}
                className={`text-[11px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${
                  draftStatus === s
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-[#6B6B6B] border-[#E5E5E5] hover:border-ink"
                }`}
              >
                {s.replace("_", " ")}
              </button>
            ))}
          </div>
          <textarea
            className="w-full text-sm border border-[#E5E5E5] rounded-md p-2 focus:outline-none focus:border-gold"
            rows={2}
            placeholder="Notes (visible to admins only)"
            value={draftNotes}
            onChange={(e) => setDraftNotes(e.target.value)}
            data-testid={`verify-step-${stepKey}-notes`}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              className="rounded-full"
              disabled={busy}
              onClick={() => onUpdate(stepKey, { status: draftStatus, notes: draftNotes })}
              data-testid={`verify-step-${stepKey}-save`}
            >
              Save step
            </Button>
            {(stepKey === "identity" || stepKey === "premises") && (
              <label
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white border border-stone-300 text-stone-800 text-[11px] font-bold uppercase tracking-[0.12em] cursor-pointer hover:bg-stone-100 transition-colors"
                data-testid={`verify-step-${stepKey}-upload-label`}
              >
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onUpload(stepKey, f);
                    e.target.value = "";
                  }}
                  data-testid={`verify-step-${stepKey}-upload`}
                />
                {busy ? "Uploading…" : (
                  stepKey === "identity"
                    ? (step.evidence_url ? "Replace ID file" : "Upload ID (JPG / PDF)")
                    : "Add premises photo"
                )}
              </label>
            )}
            {stepKey === "references" && (
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                disabled={busy}
                onClick={() => onUpdate("__refs_request__", null)}
                data-testid="verify-references-request"
              >
                <PaperPlaneTilt size={14} className="mr-1" /> Email 2 random buyers
              </Button>
            )}
          </div>
        </div>
      )}

      {automated && (
        <div className="flex items-center gap-2 text-[11px] text-[#6B6B6B]">
          <Icon as={Robot} tone="muted" size="xs" weight="duotone" />
          <span>Auto-derived from platform data — no manual override.</span>
        </div>
      )}
    </div>
  );
}

export default function KennelVerificationDrawer({ open, onOpenChange, breederId, breederName, onVerifiedChanged }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!breederId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/kennel-verification/${breederId}`);
      setDoc(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, breederId]);

  const updateStep = async (stepKey, payload) => {
    if (stepKey === "__refs_request__") {
      setBusy(true);
      try {
        const r = await api.post(`/admin/kennel-verification/${breederId}/references/request`);
        toast.success(`Sent ${r.data.sent} reference requests.`);
        await load();
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/kennel-verification/${breederId}/step/${stepKey}`, payload);
      toast.success(`Updated: ${stepKey.replace("_", " ")}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const refreshAutomated = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/kennel-verification/${breederId}/refresh-automated`);
      toast.success("Re-ran automated checks.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const uploadEvidence = async (stepKey, file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/kennel-verification/${breederId}/upload/${stepKey}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Evidence uploaded.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteEvidence = async (stepKey, url) => {
    if (!window.confirm("Remove this evidence file from the verification record?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/kennel-verification/${breederId}/upload/${stepKey}`, {
        params: { url },
      });
      toast.success("Evidence removed.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const conferSeal = async (override = false) => {
    setBusy(true);
    try {
      await api.put(`/breeders/${breederId}/verify`, { verified: true, override });
      toast.success(override ? "Seal conferred (override recorded)." : "Seal conferred — all 7 steps passed.");
      onVerifiedChanged?.(true);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revokeSeal = async () => {
    setBusy(true);
    try {
      await api.put(`/breeders/${breederId}/verify`, { verified: false });
      toast.success("Verification revoked.");
      onVerifiedChanged?.(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const allPassed = doc?.all_steps_passed;
  const verified = doc?.currently_verified;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto bg-[#FAFAFA]" data-testid="kennel-verification-drawer">
        <SheetHeader>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">Verification</p>
          <SheetTitle className="font-spectral font-light text-3xl tracking-tight">
            {breederName || "Kennel"}
          </SheetTitle>
          <SheetDescription className="text-sm text-[#6B6B6B]">
            Seven independent checks. The seal is conferred only when all seven pass — or via an audited override.
          </SheetDescription>
        </SheetHeader>

        {loading && <p className="mt-8 text-sm text-[#6B6B6B]">Loading…</p>}

        {doc && (
          <div className="mt-6 space-y-4">
            {/* Header status strip */}
            <div className={`flex items-center justify-between gap-4 p-4 rounded-xl border ${
              verified ? "bg-[#FBF7EC] border-[#E5D08A]" : "bg-white border-[#EAEAEA]"
            }`}>
              <div className="flex items-center gap-3">
                <ShieldCheck size={28} weight={verified ? "fill" : "duotone"} className={verified ? "text-[#8A6A00]" : "text-[#6B6B6B]"} />
                <div>
                  <div className="font-spectral text-lg tracking-tight">
                    {verified ? "Currently verified" : allPassed ? "All checks passed — ready to confer" : "Not yet verified"}
                  </div>
                  <div className="text-xs text-[#6B6B6B]">
                    {Object.values(doc.steps).filter((s) => s.status === "passed").length} / 7 steps passed
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="rounded-full" onClick={refreshAutomated} disabled={busy} data-testid="verify-refresh-automated">
                  <ArrowsClockwise size={14} className="mr-1" /> Refresh
                </Button>
                {verified ? (
                  <Button size="sm" variant="outline" className="rounded-full text-crimson border-crimson/40 hover:bg-crimson/5" onClick={revokeSeal} disabled={busy} data-testid="verify-revoke">
                    Revoke seal
                  </Button>
                ) : allPassed ? (
                  <Button size="sm" className="rounded-full bg-gold-gradient text-ink hover:opacity-95" onClick={() => conferSeal(false)} disabled={busy} data-testid="verify-confer">
                    Confer seal
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => conferSeal(true)} disabled={busy} data-testid="verify-confer-override">
                    Confer w/ override
                  </Button>
                )}
              </div>
            </div>

            {/* Steps */}
            {Object.entries(doc.step_labels || {}).map(([key, label]) => (
              <StepCard
                key={key}
                stepKey={key}
                label={label}
                step={doc.steps[key] || {}}
                breederId={breederId}
                onUpdate={updateStep}
                onRefresh={refreshAutomated}
                onUpload={uploadEvidence}
                onDeleteEvidence={deleteEvidence}
                onLinked={load}
                busy={busy}
              />
            ))}

            <p className="text-[11px] text-[#6B6B6B] italic text-center pt-4">
              Override conferrals are logged with the admin id and shown in the audit log on the breeder record.
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
